import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChatCircleText, X, Plus, PaperPlaneRight, CheckCircle, CaretDown } from '@phosphor-icons/react';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import ESignature from '../../ESignature';
import { errText } from '../../../utils/errors';
import { QUERY_STATUS, fmtDateTime, isQueryOpen } from './monitoringUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const NONE = '__none__';
const qText = (q) => q?.text_es || q?.text_en || q?.text || q?.question_id || '';

const NewQueryForm = ({ questions, onCreate, onCancel, lang, busy }) => {
  const es = lang === 'es';
  const [qid, setQid] = useState(NONE);
  const [text, setText] = useState('');
  return (
    <div className="rounded-sm border border-[#1f3e80]/30 bg-[#1f3e80]/[0.03] p-3 space-y-2" data-testid="qq-new-form">
      <Select value={qid} onValueChange={setQid}>
        <SelectTrigger className="h-8 text-xs bg-white" data-testid="qq-new-question-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE} className="text-xs">{es ? 'Cuestionario en general' : 'Whole questionnaire'}</SelectItem>
          {questions.map(q => (
            <SelectItem key={q.question_id} value={q.question_id} className="text-xs">{qText(q).slice(0, 80)}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Textarea data-testid="qq-new-text" value={text} onChange={e => setText(e.target.value)}
        placeholder={es ? 'Describe la duda...' : 'Describe the issue...'} className="min-h-[70px] text-sm bg-white" />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel}>{es ? 'Cancelar' : 'Cancel'}</Button>
        <Button size="sm" data-testid="qq-new-submit" disabled={busy || !text.trim()}
          onClick={() => onCreate(qid === NONE ? null : questions.find(q => q.question_id === qid), text.trim())}
          className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm">{es ? 'Levantar query' : 'Raise query'}</Button>
      </div>
    </div>
  );
};

const QueryThread = ({ query, studyId, canClose, focused, onFocus, onRefresh, onGoToQuestion, lang }) => {
  const es = lang === 'es';
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const open = isQueryOpen(query);
  const cfg = QUERY_STATUS[query.status] || QUERY_STATUS.en_curso;
  const messages = query.messages?.length ? query.messages
    : [{ id: 'orig', author_name: query.created_by_name, author_email: query.created_by, text: query.observations, created_at: query.created_at }];

  const send = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/studies/${studyId}/queries/${query.query_id}/messages`, { text: reply.trim() }, { withCredentials: true });
      setReply('');
      onRefresh();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const close = async (pin, reason) => {
    await axios.post(`${API_URL}/api/studies/${studyId}/queries/${query.query_id}/close`, { pin, reason }, { withCredentials: true });
    toast.success(es ? 'Query cerrada' : 'Query closed');
    setSignOpen(false);
    onRefresh();
  };

  return (
    <div data-testid={`qq-item-${query.code}`}
      className={`rounded-sm border bg-white transition-shadow ${focused ? 'border-[#1f3e80] shadow-[0_0_0_2px_rgba(31,62,128,0.15)]' : 'border-stone-200'}`}>
      <button type="button" onClick={() => { onFocus(query.query_id); if (query.question_id) onGoToQuestion(query.question_id); }}
        className="w-full text-left px-3 py-2 flex items-start gap-2" data-testid={`qq-item-head-${query.code}`}>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[11px] text-stone-600">{query.code}</span>
            <Badge variant="outline" className={`text-[10px] ${cfg.color}`}>{es ? cfg.es : cfg.en}</Badge>
          </div>
          <p className="text-xs text-stone-500 mt-0.5 truncate">{query.question_text || (es ? 'Cuestionario en general' : 'Whole questionnaire')}</p>
        </div>
        <CaretDown size={12} className={`mt-1 text-stone-400 transition-transform ${focused ? 'rotate-180' : ''}`} />
      </button>
      {focused && (
        <div className="border-t border-stone-100 px-3 py-2 space-y-2">
          {messages.map(m => (
            <div key={m.id} className="rounded-sm bg-stone-50 px-2.5 py-1.5" data-testid={`qq-msg-${m.id}`}>
              <p className="text-[10px] text-stone-400">{m.author_name || m.author_email} · {fmtDateTime(m.created_at, lang)}</p>
              <p className="text-sm text-stone-800 whitespace-pre-wrap">{m.text}</p>
            </div>
          ))}
          {open ? (
            <>
              <Textarea data-testid={`qq-reply-${query.code}`} value={reply} onChange={e => setReply(e.target.value)}
                placeholder={es ? 'Escribe una respuesta...' : 'Write a reply...'} className="min-h-[56px] text-sm" />
              <div className="flex justify-between gap-2">
                {canClose ? (
                  <Button size="sm" variant="outline" data-testid={`qq-close-${query.code}`} onClick={() => setSignOpen(true)}
                    className="h-7 gap-1 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"><CheckCircle size={12} />{es ? 'Cerrar' : 'Close'}</Button>
                ) : <span />}
                <Button size="sm" data-testid={`qq-send-${query.code}`} disabled={busy || !reply.trim()} onClick={send}
                  className="h-7 gap-1 text-xs bg-[#1f3e80] hover:bg-[#2a4f8a] text-white"><PaperPlaneRight size={12} />{es ? 'Responder' : 'Reply'}</Button>
              </div>
            </>
          ) : (
            <p className="text-[11px] text-emerald-700">{es ? 'Cerrada por' : 'Closed by'} {query.closed_by} · {fmtDateTime(query.closed_at, lang)}</p>
          )}
        </div>
      )}
      <ESignature open={signOpen} onClose={() => setSignOpen(false)} onSubmitPin={close}
        action={es ? 'Cerrar query' : 'Close query'} resource={query.code} lang={lang} />
    </div>
  );
};

export default function QuestionnaireQueriesPanel({
  studyId, fillQ, entryIndex, queries, questions, canCreate, canCloseQuery,
  focusQueryId, onFocus, onRefresh, onGoToQuestion, onCollapse, startNew, lang,
}) {
  const es = lang === 'es';
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  useEffect(() => { if (startNew) setCreating(true); }, [startNew]);

  const openList = queries.filter(isQueryOpen);
  const closedList = queries.filter(q => !isQueryOpen(q));

  const create = async (question, text) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/studies/${studyId}/queries/questionnaire`, {
        volunteer_id: fillQ.vol.volunteer_id,
        study_volunteer_code: fillQ.vol.study_volunteer_code || '',
        visit_id: fillQ.visit.visit_id,
        visit_code: fillQ.visit.code || '',
        questionnaire_id: fillQ.questionnaire.questionnaire_id,
        questionnaire_name: fillQ.questionnaire.name || '',
        entry_index: entryIndex || 0,
        question_id: question?.question_id || '',
        question_text: question ? qText(question) : '',
        text,
      }, { withCredentials: true });
      toast.success(es ? `Query ${data.code} creada` : `Query ${data.code} created`);
      setCreating(false);
      onFocus(data.query_id);
      onRefresh();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const item = (q) => (
    <QueryThread key={q.query_id} query={q} studyId={studyId} canClose={canCloseQuery(q)} lang={lang}
      focused={focusQueryId === q.query_id} onFocus={(id) => onFocus(focusQueryId === id ? null : id)}
      onRefresh={onRefresh} onGoToQuestion={onGoToQuestion} />
  );

  return (
    <aside className="flex flex-col min-h-0 h-full border border-stone-200 rounded-sm bg-stone-50/70" data-testid="qq-panel">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-stone-200 bg-white">
        <ChatCircleText size={16} className="text-[#1f3e80]" />
        <span className="text-sm font-semibold text-stone-800">{es ? 'Queries del cuestionario' : 'Questionnaire queries'}</span>
        <Badge variant="outline" className="text-[10px] font-mono">{openList.length}</Badge>
        <div className="ml-auto flex items-center gap-1">
          {canCreate && !creating && (
            <Button size="sm" variant="outline" data-testid="qq-new-btn" onClick={() => setCreating(true)} className="h-7 gap-1 text-xs">
              <Plus size={12} />{es ? 'Nueva' : 'New'}
            </Button>
          )}
          <button type="button" data-testid="qq-collapse-btn" onClick={onCollapse} title={es ? 'Plegar panel' : 'Collapse panel'}
            className="h-7 w-7 inline-flex items-center justify-center rounded-sm text-stone-500 hover:bg-stone-100"><X size={14} /></button>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2">
        {creating && <NewQueryForm questions={questions} onCreate={create} onCancel={() => setCreating(false)} lang={lang} busy={busy} />}
        {openList.length === 0 && closedList.length === 0 && !creating && (
          <p className="text-xs text-stone-400 text-center py-6">{es ? 'Este cuestionario no tiene queries.' : 'No queries for this questionnaire.'}</p>
        )}
        {openList.map(item)}
        {closedList.length > 0 && (
          <div className="pt-2">
            <button type="button" data-testid="qq-closed-toggle" onClick={() => setShowClosed(v => !v)}
              className="text-[11px] font-semibold uppercase tracking-wider text-stone-500 flex items-center gap-1">
              <CaretDown size={10} className={showClosed ? 'rotate-180' : ''} />{es ? 'Cerradas' : 'Closed'} ({closedList.length})
            </button>
            {showClosed && <div className="mt-2 space-y-2">{closedList.map(item)}</div>}
          </div>
        )}
      </div>
    </aside>
  );
}
